import {
    selectedDateChanged,
    selectedItemChanged,
    refreshDayGrid,
} from '../actions/privateActions';
import getFullDayFromDate from '../utils/getFullDayFromDate';
import { getLockedCalendarEventsStore } from 'owa-calendar-events-store';
import { addCalendarItems } from 'owa-calendar-surface-dategrid';
import { isEqual, startOfDay } from 'owa-datetime';
import { DateRange, getDateRangeLength } from 'owa-datetime-utils';
import { orchestrator } from 'satcheljs';
import {
    updateFolderIds,
    updateSelectionItem,
    navigateToDate,
    resetDayGrid,
    getScenarioStore,
} from 'owa-day-grid-core-schema';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { getCustomPopulateDayData } from '../utils/customPopulateDayDataMap';

async function populateDayData(
    scenarioId: string,
    newFolderIds?: string[],
    newDateRange?: DateRange,
    newSelectionItem?: CalendarEvent,
    newShowSelectionItem?: boolean
) {
    const customPopulateFunction = getCustomPopulateDayData(scenarioId);
    if (customPopulateFunction) {
        customPopulateFunction(
            scenarioId,
            newFolderIds,
            newDateRange,
            newSelectionItem,
            newShowSelectionItem
        );
        return;
    }

    const viewState = getScenarioStore(scenarioId);
    const { eventsCacheLockId, folderIds, dateRange, selectionItem, showSelectionItem } = viewState;
    newFolderIds = newFolderIds || folderIds;
    newDateRange = newDateRange || dateRange;
    newSelectionItem = newSelectionItem || selectionItem;
    newShowSelectionItem = newShowSelectionItem == undefined ? showSelectionItem : false;
    let itemsToAdd = [];

    if (newSelectionItem?.IsAllDayEvent && newShowSelectionItem) {
        itemsToAdd.push(newSelectionItem);
    }

    const eventsStore = getLockedCalendarEventsStore(eventsCacheLockId);
    for (let folderId of folderIds) {
        await eventsStore
            .fetchCalendarEventsForDateRange(folderId, dateRange)
            .fetchPromise.then(items => items && itemsToAdd.push(...items));
    }

    addCalendarItems(itemsToAdd, viewState, true /*replace all*/);
}

orchestrator(updateFolderIds, ({ scenarioId, folderIds }) => {
    populateDayData(scenarioId, folderIds);
});

orchestrator(navigateToDate, ({ scenarioId, newDate }) => {
    let newDateStart = startOfDay(newDate);
    const viewState = getScenarioStore(scenarioId);
    if (!viewState.selectedDate || !isEqual(startOfDay(viewState.selectedDate), newDateStart)) {
        selectedDateChanged(scenarioId, newDateStart);
        populateDayData(
            scenarioId,
            undefined /*FolderIds */,
            getFullDayFromDate(newDate, getDateRangeLength(viewState.dateRange) + 1)
        );
    }
});

orchestrator(updateSelectionItem, ({ scenarioId, selectionItem }) => {
    const viewState = getScenarioStore(scenarioId);
    const originalItemIsAllDay = viewState.selectionItem?.IsAllDayEvent;
    selectedItemChanged(scenarioId, selectionItem);
    if (selectionItem && originalItemIsAllDay != selectionItem.IsAllDayEvent) {
        populateDayData(scenarioId, undefined /*folderId*/, undefined /*dateRange*/, selectionItem);
    }
});

orchestrator(refreshDayGrid, ({ scenarioId, perfDatapoint }) => {
    populateDayData(scenarioId).then(() => {
        if (!perfDatapoint?.hasEnded) {
            perfDatapoint?.end();
        }
    });
});

orchestrator(resetDayGrid, ({ scenarioId }) => {
    const viewState = getScenarioStore(scenarioId);
    if (viewState?.eventsCacheLockId) {
        getLockedCalendarEventsStore(viewState.eventsCacheLockId).release();
    }
});
